<template>
  <div>
    <v-container fluid class="px-0">
      <v-row>
        <v-col cols="12" align="right">
          <ToggleButton
            class="hide-on-print"
            :state="collapseButtonState"
            color="primary"
            :statesLabel="collapseButtonLabels"
            @click="(state) => this.collapseButtonState = state === 1 ? 2 : 1 "
          ></ToggleButton>
        </v-col>
      </v-row>
      <v-row>
        <v-col cols="12" class="py-0">
          <v-layout column>
            <v-data-table
              dense
              :headers="headers"
              :items="clientBillingSummaries"
              item-key="clientId"
              :sort-by="['clientName']"
              :sort-desc="[false]"
              class="elevation-1"
              :expanded="expanded"
              hide-default-footer
              show-expand
              :items-per-page="-1"
            >
            <template v-slot:item.totalBillableHours="{ item }">
              {{ item.totalBillableHours | formatDecimal }}
            </template>
            <template v-slot:item.totalBillAmount="{ item }">
              {{ item.totalBillAmount | currency }}
            </template>
            <template v-slot:expanded-item="{ headers, item }">
              <td class="py-5" :colspan="headers.length">
                <v-data-table
                    dense
                    :headers="subHeaders"
                    :items="item.billingSummaries"
                    class="pb-3 px-5"
                >
                  <template v-slot:item.billRate="{ item }">
                    {{ item.billRate | currency }} {{ item.billRateTypeId | getBillRateTypeLabel }}
                  </template>
                  <template v-for="col in typeColumns" v-slot:[col.hoursSlot]="{ item }">
                    {{ getTypeHours(item, col.timeTypeId) | formatDecimal }}
                  </template>
                  <template v-for="col in typeColumns" v-slot:[col.amountSlot]="{ item }">
                    {{ getTypeAmount(item, col.timeTypeId) | currency }}
                  </template>
                  <template v-slot:item.totalBillableHours="{ item }">
                    {{ item.totalBillableHours | formatDecimal }}
                  </template>
                  <template v-slot:item.totalContractorCommissionWithMarkup="{ item }">
                    {{ item.totalContractorCommissionWithMarkup | currency }}
                  </template>
                  <template v-slot:item.totalBill="{ item }">
                    <span :class="{
                      'blue-highlighted' : item.totalBillEdited === true,
                      'yellow-highlighted': item.needsToBeProrated === true
                    }">{{ item.totalBill | currency }}</span>
                    <v-tooltip v-if="item.totalBillEdited === true" bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-icon small color="primary" class="ml-1 hide-on-print" v-bind="attrs" v-on="on">lock</v-icon>
                      </template>
                      <span>Frozen at {{ item.totalBill | currency }}. Typed hours currently compute {{ item.computedTotalBill | currency }}.</span>
                    </v-tooltip>
                  </template>
                  <template v-slot:item.actions="{ item }">
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-1 my-1 hide-on-print"
                          color="primary"
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click.prevent="handleClickEditHours($event, item)"
                        >
                          <v-icon color="primary">edit</v-icon>
                        </v-btn>
                      </template>
                      <span>Edit hours</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-1 my-1 hide-on-print"
                          color="primary"
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click.prevent="handleClickViewTimeCard($event, item)"
                        >
                          <v-icon color="primary">schedule</v-icon>
                        </v-btn>
                      </template>
                      <span>Go to time card</span>
                    </v-tooltip>
                    <v-tooltip bottom>
                      <template v-slot:activator="{ on, attrs }">
                        <v-btn
                          class="mx-1 my-1 hide-on-print"
                          color="primary"
                          outlined
                          v-bind="attrs"
                          v-on="on"
                          @click="handleClickViewOneOffFees(item)"
                        >
                          <v-icon color="primary">price_check</v-icon>
                        </v-btn>
                      </template>
                      <span>View one off fees</span>
                    </v-tooltip>
                  </template>
                </v-data-table>
              </td>
            </template>
            </v-data-table>
          </v-layout>
        </v-col>
      </v-row>
    </v-container>
    <OneOffFeesRecordListModal
      :assignmentId="modalAssignmentId"
      :userName="modalUserName"
      :clientName="modalClientName"
      :showDateRangePicker="false"
      :filterDateBegin="filterDateBegin"
      :filterDateEnd="filterDateEnd"
      @closeModal="$emit('OneOffFeeListModalClose')"
      ref="OneOffFeesRecordListModal"
    >
    </OneOffFeesRecordListModal>
  </div>
</template>

<script>

import { timeCardReportRoute, timeManagementRoute } from '@/lib/constants/routes'
import { OneOffFeesRecordListModal } from '@/components/oneOffFeesRecords'
import { mapFields } from '@/vueHelpers/vuex'
import { getPresentTypeSummaries, getTypeSummaryHours, getTypeSummaryAmount, TypeSummaryAmountField } from '@/lib/reports'
import { ToggleButton } from '@/components/buttons/'

export default {
  components: {
    ToggleButton,
    OneOffFeesRecordListModal
  },
  props: [
    'clientBillingSummaries',
    'businessEntityId'
  ],
  data () {
    return {
      headers: [
        { text: 'Client', value: 'clientName', sortable: false, cellClass: 'py-3' },
        { text: 'Total Billable Hours', value: 'totalBillableHours', align: 'center', sortable: false },
        { text: 'Total Bill Amount', value: 'totalBillAmount', align: 'center', sortable: false }
      ],
      modalAssignmentId: null,
      modalUserName: null,
      modalClientName: null,
      filterDateBegin: null,
      filterDateEnd: null,
      collapseButtonLabels: {
        1: 'Contract All',
        2: 'Expand All'
      },
      collapseButtonState: 1 // 1 - Expanded, 2 - Collapsed
    }
  },
  computed: {
    ...mapFields('invoicingReportFilters', [
      'dateBegin',
      'dateEnd',
      'showDetails'
    ]),
    // Report-wide, not per-row: a type absent from one client's rows still gets its columns if
    // any other row in the report carries it, so a single header array serves every expanded
    // client sub-table.
    presentTypeSummaries () {
      const allSummaries = (this.clientBillingSummaries || []).flatMap(client => client.billingSummaries || [])
      return getPresentTypeSummaries(allSummaries)
    },
    typeColumns () {
      return this.presentTypeSummaries.map(type => ({
        timeTypeId: type.timeTypeId,
        hoursSlot: `item.type_${type.timeTypeId}_hours`,
        amountSlot: `item.type_${type.timeTypeId}_amount`,
        hoursHeader: { text: type.timeTypeName, value: `type_${type.timeTypeId}_hours`, width: '100px', align: 'center' },
        amountHeader: { text: `${type.timeTypeName} Bill`, value: `type_${type.timeTypeId}_amount`, width: '100px', align: 'center' }
      }))
    },
    subHeaders () {
      let headers = []
      if (this.showDetails) {
        headers = [
          { text: 'Consultant', value: 'userFullName', width: '150px' },
          { text: 'Standard Rate', value: 'billRate', width: '150px' },
          ...this.typeColumns.flatMap(col => [col.hoursHeader, col.amountHeader]),
          { text: 'Total Billable Hours', value: 'totalBillableHours', width: '170px' },
          { text: 'Contractor Commission w/ Markup', value: 'totalContractorCommissionWithMarkup', width: '170px' },
          { text: 'Total Bill', value: 'totalBill', width: '100px', class: 'px-2', cellClass: 'px-2' },
          { text: 'Actions', value: 'actions', align: 'center', sortable: false, class: 'hide-on-print', cellClass: 'hide-on-print px-0', width: '250px' }
        ]
      } else {
        headers = [
          { text: 'Consultant', value: 'userFullName', width: '20%' },
          { text: 'Standard Rate', value: 'billRate', width: '18%' },
          { text: 'Total Billable Hours', value: 'totalBillableHours', width: '20%' },
          { text: 'Total Bill', value: 'totalBill', width: '15%', class: 'px-2', cellClass: 'px-2' },
          { text: 'Actions', value: 'actions', align: 'center', sortable: false, class: 'hide-on-print', cellClass: 'hide-on-print px-0', width: '27%' }
        ]
      }
      return headers
    },
    expanded: {
      get () {
        if (this.collapseButtonState === 1) {
          return this.clientBillingSummaries || []
        } else {
          return []
        }
      }
    }
  },
  methods: {
    getTypeHours (item, timeTypeId) {
      return getTypeSummaryHours(item, timeTypeId)
    },
    getTypeAmount (item, timeTypeId) {
      return getTypeSummaryAmount(item, timeTypeId, TypeSummaryAmountField.BILL)
    },
    handleClickViewTimeCard (event, item) {
      let assignmentId = item.assignmentId
      let startDate = this.dateBegin
      let endDate = this.dateEnd
      const routeData = this.$router.resolve({
        name: timeCardReportRoute.name,
        query: { startDate: startDate, endDate: endDate, assignmentId: assignmentId }
      })
      if (event.ctrlKey === true) {
        window.open(routeData.href, '_blank')
      } else {
        window.open(routeData.href, '_self')
      }
    },
    handleClickViewOneOffFees (item) {
      this.modalAssignmentId = item.assignmentId
      this.modalUserName = item.userFullName
      this.modalClientName = item.clientName
      this.filterDateBegin = item.invoicingPeriodDateBegin
      this.filterDateEnd = item.invoicingPeriodDateEnd
      this.$refs.OneOffFeesRecordListModal.showModal()
    },
    handleClickEditHours (event, item) {
      const periodEnd = new Date(item.invoicingPeriodDateEnd)
      const month = `${periodEnd.getFullYear()}-${String(periodEnd.getMonth() + 1).padStart(2, '0')}`
      const query = { viewMode: 'month', client: item.clientId, consultant: item.userId, assignment: item.assignmentId, month: month }
      if (this.businessEntityId > 0) {
        query.businessEntityId = this.businessEntityId
      }
      const routeData = this.$router.resolve({
        name: timeManagementRoute.name,
        query
      })
      if (event.ctrlKey === true) {
        window.open(routeData.href, '_blank')
      } else {
        window.open(routeData.href, '_self')
      }
    }
  }
}
</script>
